// src/pages/AdminPage/tabs/VideosTab.tsx
import { useEffect, useState, useCallback, useMemo } from "react";
import { Video, Edit2, Trash2, Plus, Film, Youtube } from "lucide-react";
import { adminAPI } from "../utils/adminApi";
import SearchBar from "../components/SearchBar";
import type { VideoItem } from "../utils/adminTypes";

const IMG_BASE = process.env.REACT_APP_BASE_URL_IMAGE;

interface VideosTabProps {
  onCreate: () => void;
  onEdit: (v: VideoItem) => void;
  onDelete: (id: number) => void;
  onError: (msg: string) => void;
}

export default function VideosTab({
  onCreate,
  onEdit,
  onDelete,
  onError,
}: VideosTabProps) {
  const [items, setItems] = useState<VideoItem[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    adminAPI
      .getVideos()
      .then((res) => setItems(res.data || []))
      .catch(() => onError("Videolarni yuklashda xatolik"))
      .finally(() => setLoading(false));
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((v) => v.title.toLowerCase().includes(q));
  }, [items, search]);

  const isExternal = (url: string | null) =>
    !!url && /^https?:\/\//i.test(url);

  return (
    <div className="fade-up">
      <div
        style={{
          display: "flex",
          gap: 10,
          marginBottom: 16,
          flexWrap: "wrap",
        }}>
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Sarlavha bo'yicha qidirish..."
        />
        <button
          onClick={onCreate}
          className="btn-hover"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "10px 16px",
            borderRadius: 12,
            border: "none",
            background: "#3B82F6",
            color: "#fff",
            fontWeight: 700,
            fontSize: 13,
            cursor: "pointer",
          }}>
          <Plus size={15} /> Yangi video
        </button>
        <div
          style={{
            color: "#64748B",
            fontSize: 13,
            padding: "10px 14px",
            background: "#111827",
            border: "1px solid #1E2D45",
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}>
          <Video size={14} /> Jami:{" "}
          <strong style={{ color: "#fff" }}>{filtered.length}</strong>
        </div>
      </div>

      {loading ?
        <p style={{ color: "#64748B", fontSize: 13 }}>Yuklanmoqda...</p>
      : filtered.length === 0 ?
        <p style={{ color: "#64748B", fontSize: 13 }}>Videolar topilmadi</p>
      : <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 14,
          }}>
          {filtered.map((v) => (
            <div
              key={v.id}
              style={{
                background: "#111827",
                border: "1px solid #1E2D45",
                borderRadius: 14,
                overflow: "hidden",
              }}>
              <div
                style={{
                  width: "100%",
                  height: 120,
                  background: "#0D1526",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                {v.thumbnail_url ?
                  <img
                    src={`${IMG_BASE}/${v.thumbnail_url}`}
                    alt={v.title}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                : <Film size={26} color="#475569" />}
              </div>
              <div style={{ padding: 12 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 6,
                  }}>
                  <span
                    style={{
                      padding: "2px 8px",
                      borderRadius: 99,
                      fontSize: 10,
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      background:
                        isExternal(v.video_url) ?
                          "rgba(239,68,68,.15)"
                        : "rgba(59,130,246,.15)",
                      color: isExternal(v.video_url) ? "#EF4444" : "#3B82F6",
                    }}>
                    {isExternal(v.video_url) ?
                      <>
                        <Youtube size={10} /> YouTube
                      </>
                    : <>
                        <Film size={10} /> Fayl
                      </>
                    }
                  </span>
                  <span style={{ color: "#475569", fontSize: 11 }}>
                    #{v.order_index}
                  </span>
                </div>
                <div
                  style={{
                    color: "#E2E8F0",
                    fontWeight: 700,
                    fontSize: 13,
                    marginBottom: 10,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}>
                  {v.title}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    onClick={() => onEdit(v)}
                    title="Tahrirlash"
                    style={{
                      flex: 1,
                      padding: "7px",
                      borderRadius: 8,
                      border: "1px solid rgba(59,130,246,.3)",
                      background: "rgba(59,130,246,.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}>
                    <Edit2 size={12} color="#3B82F6" />
                  </button>
                  <button
                    onClick={() => onDelete(v.id)}
                    title="O'chirish"
                    style={{
                      flex: 1,
                      padding: "7px",
                      borderRadius: 8,
                      border: "1px solid rgba(239,68,68,.3)",
                      background: "rgba(239,68,68,.08)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}>
                    <Trash2 size={12} color="#EF4444" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      }
    </div>
  );
}
